import React, { useMemo } from 'react';
import { useFullRelease } from '@/common/hooks_useFullRelease';
import { flattenReleaseDocuments, type ReleaseDocument } from '@packages/commons';
import { useCommandPaletteContext } from '../CommandPaletteContext';
import { CommandList } from '../components/CommandList';
import {
  useRegisterCommands,
  useRegisterPage,
  useRegisterShortcut,
  usePluginNavigation,
} from '../hooks';
import { useNavigateToDocument, sanitizeDocName } from '@/hooks/documents';
import type { Command as CommandType } from '../types';
import { getFileIcon } from '@/hooks/documents';

const PLUGIN_ID = 'documents-plugin';
const DOCUMENTS_PAGE = 'documents';
const DOCUMENTS_PLACEHOLDER = 'Search documents...';

// ---------------------------------------------------------------------------
// useReleaseDocuments — internal hook to get the flat document list from the
// current release. Memoised against the release documents reference.
// ---------------------------------------------------------------------------

const useReleaseDocuments = (): ReleaseDocument[] => {
  const release = useFullRelease();
  return useMemo(
    () => flattenReleaseDocuments(release?.documents)
      .map(doc => ({
        ...doc,
        name: sanitizeDocName(doc.name),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [release?.documents],
  );
};

// ---------------------------------------------------------------------------
// buildDocumentCommands — reusable builder function.
// Accepts a flat document list and the callbacks needed to build each command.
// Filtering is delegated to CommandList. Returns a Command[].
// ---------------------------------------------------------------------------

export const buildDocumentCommands = (
  documents: ReleaseDocument[],
  navigateToDocument: (path: string) => void,
  close: () => void,
): CommandType[] => {
  return documents.map(doc => {
    const lastSlash = doc.name.lastIndexOf('/');
    const label = lastSlash >= 0 ? doc.name.slice(lastSlash + 1) : doc.name;
    const dir = lastSlash > 0 ? doc.name.slice(0, lastSlash) : undefined;
    const description = [dir, doc.type].filter(Boolean).join(' · ');

    return {
      id: `document-${doc.reference}`,
      label,
      description,
      icon: getFileIcon(doc),
      category: 'Documents',
      keywords: [doc.name, doc.type],
      primary: {
        id: `document-${doc.reference}-open`,
        label: 'Open Document',
        action: () => {
          navigateToDocument(doc.name);
          close();
        },
      },
    };
  });
};

// ---------------------------------------------------------------------------
// DocumentsPage — rendered when the palette navigates to DOCUMENTS_PAGE
// ---------------------------------------------------------------------------

const DocumentsPage: React.FC = () => {
  const { close } = useCommandPaletteContext();
  const documents = useReleaseDocuments();
  const navigateToDocument = useNavigateToDocument();

  const commands = useMemo(
    () => buildDocumentCommands(documents, navigateToDocument, close),
    [documents, navigateToDocument, close],
  );

  return <CommandList delay={300} commands={commands} emptyMessage="No documents found." />;
};

// ---------------------------------------------------------------------------
// DocumentsPlugin — registers the global command and keyboard shortcut
// ---------------------------------------------------------------------------

export const DocumentsPlugin: React.FC = () => {
  const { navigateTo, openAndNavigate } = usePluginNavigation(PLUGIN_ID);

  const globalCommands = useMemo((): CommandType[] => [
    {
      id: 'open-document-search',
      label: 'Go to Document',
      description: 'Search and navigate to a document',
      category: 'Documents',
      shortcut: 'mod+shift+d',
      keywords: ['document', 'navigate', 'search', 'file', 'open'],
      primary: {
        id: 'open-document-search-action',
        label: 'Search Documents',
        action: () => navigateTo(DOCUMENTS_PAGE, DOCUMENTS_PLACEHOLDER),
      },
    },
  ], [navigateTo]);

  useRegisterCommands({ pluginId: PLUGIN_ID, commands: globalCommands });
  useRegisterPage({ pluginId: PLUGIN_ID, pageId: DOCUMENTS_PAGE, component: DocumentsPage });

  useRegisterShortcut({
    shortcut: 'mod+shift+d',
    label: 'Go to Document',
    handler: () => openAndNavigate(DOCUMENTS_PAGE, DOCUMENTS_PLACEHOLDER),
  });

  return null;
};
